<template>
  <Transition name="modal" appear>
    <section
      class="modal"
      ref="backdrop"
      role="dialog"
      aria-modal="true"
      v-bind:aria-label="ariaLabel || null"
      v-bind:aria-labelledby="ariaLabelledby || null"
      v-on:click="handleBackdropClick"
      v-on:keydown.esc="handleEscape"
    >
      <!-- tabindex="-1" makes the panel programmatically focusable, which focus-trap's
           fallbackFocus requires for the handful of modals with no focusable content.
           It also gives the Escape keydown above something to bubble from. -->
      <div class="modal--inner" v-bind:class="wideClass" ref="panel" tabindex="-1">
        <slot />
      </div>
    </section>
  </Transition>
</template>

<script>
import { createFocusTrap } from "focus-trap";
import { lockBodyScroll, unlockBodyScroll } from "@/utils/scrollLock.js";

export default {
  name: "BaseModalDialog",

  props: {
    wide: {
      type: Boolean,
      default: false,
    },

    // Opt-in. Callers control visibility with v-if in the parent, so the modal cannot
    // close itself unless the parent listens for `close`. Left false by default so that
    // modals which must NOT be escapable — password expired, session timeout — keep their
    // current behaviour without any change at the call site.
    dismissible: {
      type: Boolean,
      default: false,
    },

    // Accessible name. Prefer ariaLabelledby pointing at the id of the modal's own
    // heading; fall back to a literal ariaLabel string where there is no heading.
    ariaLabel: {
      type: String,
      default: "",
    },

    ariaLabelledby: {
      type: String,
      default: "",
    },
  },

  emits: ["close"],

  data() {
    return {
      focusTrap: null,
    };
  },

  computed: {
    wideClass() {
      if (!this.wide) {
        return "";
      }
      return "modal--wide";
    },
  },

  mounted() {
    lockBodyScroll();

    // fallbackFocus targets the panel so the trap still activates when a modal contains
    // no focusable element at all (a few are text-only). allowOutsideClick lets the
    // backdrop handler below run instead of the trap swallowing the click.
    this.focusTrap = createFocusTrap(this.$refs.backdrop, {
      escapeDeactivates: false, // handled here, so non-dismissible modals stay open
      allowOutsideClick: true,
      fallbackFocus: () => this.$refs.panel,
      returnFocusOnDeactivate: true, // restores focus to whatever opened the modal
    });

    try {
      this.focusTrap.activate();
    } catch (error) {
      // Never let a focus-management failure prevent the modal from rendering.
      console.warn("Modal focus trap could not be activated", error);
      this.focusTrap = null;
    }
  },

  beforeUnmount() {
    if (this.focusTrap) {
      this.focusTrap.deactivate();
      this.focusTrap = null;
    }
    unlockBodyScroll();
  },

  methods: {
    handleEscape() {
      if (this.dismissible) {
        this.$emit("close");
      }
    },

    // Only a click on the backdrop itself closes. Clicks on the panel bubble up to this
    // handler too, so the target check is what stops an in-panel click from dismissing.
    handleBackdropClick(event) {
      if (this.dismissible && event.target === this.$refs.backdrop) {
        this.$emit("close");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// Opacity and transform only — no colour is involved, and both are GPU-composited so the
// animation does not trigger layout. Neutralised by the global prefers-reduced-motion
// guard in scss/base/_base.scss.
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .modal--inner,
.modal-leave-active .modal--inner {
  transition: transform 0.2s ease;
}

.modal-enter-from .modal--inner,
.modal-leave-to .modal--inner {
  transform: translateY(8px);
}
</style>
